import { useEffect, useState } from "react";
import { Heart, Crown, Server } from "lucide-react";
import { api } from "../lib/api";

const DONATE_URL = "https://www.patreon.com/cw/OCEDOGS";

const monthName = (mk) => {
  if (!mk) return "";
  const [y, m] = mk.split("-");
  return `${new Date(Number(y), Number(m) - 1, 1).toLocaleString("en-AU", { month: "long" })} ${y}`;
};

const DonationThermometer = ({ className = "" }) => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/donations/summary").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return null;
  const pct = Math.min(100, d.percent ?? 0);
  const funded = pct >= 100;
  return (
    <div data-testid="donation-thermometer" className={`glass tactical-frame p-5 border border-[#D4A94A]/25 ${className}`}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="overline"><Server className="inline w-3 h-3 mr-1" /> Server costs · {monthName(d.month_key)}</div>
          <h3 className="font-heading text-lg font-black uppercase leading-none mt-1">Keep Server Online</h3>
        </div>
        {funded && <span data-testid="donation-funded" className="text-[9px] font-mono font-bold bg-[#2ECC71] text-black px-2 py-1 uppercase">Funded</span>}
      </div>

      <div className="flex items-end justify-between mb-1.5 font-mono">
        <span className="text-[#F1F5F9] text-lg font-black tabular-nums" data-testid="donation-raised">${(d.raised ?? 0).toLocaleString()} <span className="text-[#64748B] text-xs font-normal">of ${(d.goal ?? 0).toLocaleString()} {d.currency}</span></span>
        <span className="text-[#D4A94A] text-sm font-bold" data-testid="donation-percent">{pct}%</span>
      </div>
      <div className="h-3 bg-[#0B0C0E] border border-[#2A2E3D] overflow-hidden mb-4">
        <div className="h-full transition-all duration-700" style={{ width: `${pct}%`, background: funded ? "#2ECC71" : "#D4A94A" }} />
      </div>

      <a href={DONATE_URL} target="_blank" rel="noopener noreferrer" data-testid="donation-donate-cta" className="w-full flex items-center justify-center gap-2 bg-[#D4A94A] hover:bg-[#E5BD60] text-black font-stencil font-black uppercase text-sm py-2.5 tactical-frame-sm transition-colors">
        <Heart className="w-4 h-4" /> Donate to the Server
      </a>
      <p className="font-mono text-[10px] text-[#64748B] mt-2 text-center">Donations credit our BisectHosting account &amp; keep the OCE server running.</p>

      {d.donors?.length > 0 && (
        <div className="mt-4 pt-3 border-t border-[#2A2E3D]" data-testid="donation-wall">
          <div className="overline text-[9px] mb-2">// Top Supporters</div>
          <div className="space-y-1">
            {d.donors.slice(0, 5).map((dn, i) => (
              <div key={`${dn.callsign}-${dn.amount}-${i}`} data-testid={`donor-row-${i}`} className="flex items-center gap-2 font-mono text-xs">
                {i === 0 ? <Crown className="w-3.5 h-3.5 text-[#D4A94A] shrink-0" /> : <span className="w-3.5 text-center text-[#64748B] shrink-0">{i + 1}</span>}
                <span className="flex-1 min-w-0 truncate font-stencil font-bold uppercase text-[#F1F5F9]">{dn.callsign}</span>
                <span className="text-[#2ECC71]">${(dn.amount ?? 0).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DonationThermometer;
